import { useState, useMemo } from 'react';
import { message } from 'antd';

import AddFallback from './AddFallback';
import { useStores } from '@/hooks/use-stores';
import { EngineConfigStatus } from '@/store/Engine/constants';
import EPModal from '@/comp/EPModal';
import RulesGeneratorModal from '@/comp/pages/EnginePage/Engine/Common/RulesGeneratorModal/RulesGeneratorModal';
import { FallbackRule } from '@/store/Engine/FallbackScenarios';
import { ModeOfFallbackCreation, NewFallbackStep } from '@/constants/FallbackEnum';
import { fallbackConstants, defaultFilterCondition, fieldOptions } from '@/constants/Fallback';
import { FilterCondition } from './types';
import {
  addCondition,
  updateCondition,
  removeCondition,
  checkForDuplicateScenarios,
  appliedCompressorsValuesArray,
  getTablesToshow,
  validateFilters,
  checkIfAllCompressorsAreSelected,
} from './Fallbacks.utils';
import { sortSetpointsByElementIndex } from '@/store/Engine/utils';
import { useTranslation } from 'react-i18next';
import { useMeasurement } from '@/hooks/useMeasurement';
import { RenderFilterStep, RenderTableSelectionStep } from './NewFallback.utils';
import { Setpoint } from '@/store/Engine/common';

interface CreateFallbackPageProps {
  configurationStatus?: EngineConfigStatus;
  onCreateFallback?: (data: {
    rangeParentKey?: string | null;
    rulesData?;
    mode: ModeOfFallbackCreation;
    appliedFilters?: FilterCondition[];
  }) => void;
}
const CreateFallbackPage = ({
  configurationStatus = EngineConfigStatus.Draft,
  onCreateFallback,
}: CreateFallbackPageProps) => {
  const { engineStore, devicesStore } = useStores();
  const [mode, setMode] = useState<ModeOfFallbackCreation | null>(null);
  const [scenarioName, setScenarioName] = useState<string>('');
  const [selectedRangeParentKey, setSelectedRangeParentKey] = useState<string>('');
  const [rulesGeneratorModalData, setRulesGeneratorModalData] = useState({
    open: false,
    data: undefined,
  });
  const [hasTriedToSubmit, setHasTriedToSubmit] = useState(false);
  const [showAddFallback, setShowAddFallback] = useState(false);
  const [rangeParentKeyForAddFallback, setRangeParentKeyForAddFallback] = useState<string | null>(null);
  const [smartRulesDataForAddFallback, setSmartRulesDataForAddFallback] = useState<Setpoint[]>(null);
  const [appliedFiltersForAddFallback, setAppliedFiltersForAddFallback] = useState([]);
  const [conditions, setConditions] = useState<FilterCondition[]>([defaultFilterCondition]);
  const [appliedFilters, setAppliedFilters] = useState<FilterCondition[]>([]);
  const [currentStep, setCurrentStep] = useState<NewFallbackStep>(NewFallbackStep.Filters);
  const { tvalue } = useMeasurement();
  const { t } = useTranslation();

  const setPointsConfiguration = engineStore.setpointsConfiguration?.getUniqueRangeSetpoints();
  const tablesToShow = getTablesToshow(setPointsConfiguration);

  const compressorOptions = useMemo(() => {
    return engineStore.applicableCompressorsForEngine.map(compressor => ({
      label: compressor.name,
      value: compressor.id,
    }));
  }, [engineStore.applicableCompressorsForEngine]);

  const addConditionHandler = () => {
    const newConditions = addCondition(conditions);
    setConditions(newConditions);
  };

  const updateConditionHandler = (id: number, key: keyof FilterCondition, val) => {
    const updatedConditions = updateCondition(conditions, id, key, val, setAppliedFilters);
    setConditions(updatedConditions);
  };

  const removeConditionHandler = (id: number) => {
    const updatedConditions = removeCondition(conditions, id, setAppliedFilters);
    setConditions(updatedConditions);
  };

  const handleApplyFilters = () => {
    const validationMessage = validateFilters(conditions);
    if (validationMessage !== '') {
      return message.error(t(validationMessage));
    }
    const validConditions = conditions.filter(c => c.field);
    setAppliedFilters(validConditions);
    setAppliedFiltersForAddFallback(validConditions);
    const compressors = appliedCompressorsValuesArray(validConditions);
    if (checkIfAllCompressorsAreSelected(compressors, devicesStore?.sortedCompressors)) {
      return message.error(t('fallbackScenarioForAllCompressorsFailureCannotBeDefined'));
    }
    if (checkForDuplicateScenarios(engineStore.fallbackScenarios.scenarios, compressors, validConditions)) {
      return message.error(t('thisScenarioAlreadyExists'));
    }
    setCurrentStep(NewFallbackStep.TableSelection);
    return validConditions;
  };

  const onOpenRulesGeneratorModal = () => {
    engineStore.clearValidationMessages();
    const newFallbackRule = new FallbackRule();
    setRulesGeneratorModalData({ open: true, data: newFallbackRule });
  };

  const onModalClose = () => {
    if (rulesGeneratorModalData.data?.hasRuleChanges()) rulesGeneratorModalData?.data?.cancelRuleChanges();
    setRulesGeneratorModalData({ open: false, data: undefined });
  };

  const onRuleGeneratorSaveClick = () => {
    const sortedRulesData = sortSetpointsByElementIndex(
      rulesGeneratorModalData?.data?.rules?.map(rule => ({ ...rule })) || [],
      devicesStore?.sortedCompressors,
    );

    rulesGeneratorModalData?.data?.setField(fallbackConstants.rules, sortedRulesData);
    setRulesGeneratorModalData({ open: false, data: undefined });

    if (onCreateFallback) {
      onCreateFallback({
        mode: ModeOfFallbackCreation.Custom,
        rulesData: sortedRulesData,
        appliedFilters: appliedFilters,
      });
    } else {
      setRangeParentKeyForAddFallback(null);
      setSmartRulesDataForAddFallback(sortedRulesData);
      setShowAddFallback(true);
    }
  };

  const isExistingMode = mode === ModeOfFallbackCreation.Existing;
  const isSmartMode = mode === ModeOfFallbackCreation.Custom;

  const handleAddFallbackClose = () => {
    setShowAddFallback(false);
  };

  const handleAddFallbackSaveSuccess = async () => {
    await engineStore.getFallbackScenarios(
      configurationStatus,
      engineStore.pagination.page,
      engineStore.pagination.pageSize,
      '',
      false,
      false,
      tvalue,
    );
  };

  const handleCreate = () => {
    if (isExistingMode) {
      if (onCreateFallback) {
        onCreateFallback({
          mode: ModeOfFallbackCreation.Existing,
          rangeParentKey: selectedRangeParentKey,
          appliedFilters: appliedFilters,
        });
      } else {
        setRangeParentKeyForAddFallback(selectedRangeParentKey);
        setSmartRulesDataForAddFallback(null);
        setShowAddFallback(true);
      }
    } else if (isSmartMode) {
      onOpenRulesGeneratorModal();
    }
  };

  if (showAddFallback) {
    return (
      <AddFallback
        onClose={handleAddFallbackClose}
        rangeParentKey={rangeParentKeyForAddFallback}
        smartRulesData={smartRulesDataForAddFallback}
        appliedFilters={appliedFiltersForAddFallback}
        onSaveSuccess={handleAddFallbackSaveSuccess}
      />
    );
  }

  const shouldShowAddButton =
    Array.isArray(conditions) &&
    conditions?.length > 0 &&
    conditions[0]?.field &&
    conditions?.length < fieldOptions.length;

  const filterStepComponent = (
    <RenderFilterStep
      t={t}
      conditions={conditions}
      compressorOptions={compressorOptions}
      shouldShowAddButton={shouldShowAddButton}
      updateConditionHandler={updateConditionHandler}
      removeConditionHandler={removeConditionHandler}
      addConditionHandler={addConditionHandler}
      handleApplyFilters={handleApplyFilters}
    />
  );

  const tableSelectionStepComponent = (
    <RenderTableSelectionStep
      t={t}
      mode={mode}
      scenarioName={scenarioName}
      tablesToShow={tablesToShow}
      isExistingMode={isExistingMode}
      isSmartMode={isSmartMode}
      selectedRangeParentKey={selectedRangeParentKey}
      setMode={setMode}
      setScenarioName={setScenarioName}
      setSelectedRangeParentKey={setSelectedRangeParentKey}
      setCurrentStep={setCurrentStep}
      handleCreate={handleCreate}
    />
  );

  return (
    <>
      {currentStep === NewFallbackStep.Filters ? filterStepComponent : tableSelectionStepComponent}

      <EPModal
        open={rulesGeneratorModalData?.open}
        onCancel={onModalClose}
        width="1032"
        height="600"
        centered
        closable={false}
        maskClosable={false}
        styles={{ body: { padding: 0 } }}
      >
        <RulesGeneratorModal
          rulesObject={rulesGeneratorModalData?.data}
          onOk={onRuleGeneratorSaveClick}
          hasTriedToSubmit={hasTriedToSubmit}
          setHasTriedToSubmit={setHasTriedToSubmit}
          disabledCompressors={appliedCompressorsValuesArray(appliedFilters)}
          isNewFallbackScenario={true}
        />
      </EPModal>
    </>
  );
};

export default CreateFallbackPage;
